import { z } from 'zod';
import { type AppRoute, initContract } from '@ts-rest/core';
import { SearchEntityQuery, ObjectIdSchema, ObjectIdString } from '@repo/common/schemas';
import {
  DataIqCheckForUpdateOptions,
  OperationDetailDTO,
  OperationStepName,
} from '@repo/common/schemas/models';
import { LookupCandidateParams, LookupContactParams } from '../apiSchemas.js';
import { ping } from './shared.js';
import { BullhornApiMeta } from '@repo/common/schemas/external/bullhorn';
import { DataIqReplayStepOptions } from '@repo/common/schemas/api';
import { ActivityType } from '@repo/common/interfaces';

const c = initContract();

const oldAdminEndpoints = {
  lookupCandidate: {
    method: 'POST',
    path: '/lookupCandidate',
    body: LookupCandidateParams,
    responses: {
      200: z.object({ result: z.unknown() }),
    },
  },
  getTextsForContact: {
    method: 'POST',
    path: '/getTextsForContact',
    body: LookupContactParams,
    responses: {
      200: z.object({ result: z.unknown() }),
    },
  },
  lookupRecruiterTest: {
    method: 'POST',
    path: '/lookupRecruiter',
    body: z.object({
      account: ObjectIdString,
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      email: z.string().optional(),
      sourceId: z.string().optional(),
      username: z.string().optional(),
      // _id: ObjectIdString.optional(),
      refreshIfStale: z.boolean().optional(),
    }),
    responses: {
      200: z.object({ result: z.unknown() }),
    },
  },
  testBullhornPostRequest: {
    method: 'POST',
    path: '/bhRequest/:accountId/:path(*)',
    pathParams: z.object({
      accountId: ObjectIdString,
      path: z.string(),
    }),
    body: z.unknown(),
    responses: {
      200: z.unknown(),
    },
  },
  testBullhornGetRequest: {
    method: 'GET',
    path: '/bhRequest/:accountId/:path(*)',
    pathParams: z.object({
      accountId: ObjectIdString,
      path: z.string(),
    }),
    responses: {
      200: z.unknown(),
    },
  },
  testCron: {
    method: 'POST',
    path: '/testCron',
    body: z.object({
      cronName: z.string(),
      cronOptions: z.object({}).passthrough().optional(),
    }),
    responses: {
      200: z.unknown(),
    },
  },
  dataIqBatch: {
    method: 'POST',
    path: '/dataIqBatch',
    body: z.object({
      accountId: ObjectIdString,
      candidateIds: z.string().array(),
    }),
    responses: {
      200: z.unknown(),
    },
  },

  initializeActivityMappings: {
    method: 'POST',
    path: '/initializeActivityMappings/:integrationId',
    pathParams: z.object({
      integrationId: ObjectIdString,
    }),
    body: null,
    responses: {
      200: z.unknown(),
    },
  },
  addRecruitersToAllNotes: {
    method: 'POST',
    path: '/addRecruitersToAllNotes/:integrationId',
    pathParams: z.object({
      integrationId: ObjectIdString,
    }),
    body: null,
    responses: {
      200: z.unknown(),
    },
  },
  // // Disabled because it's not safe to run at our current data scale
  // reprocessNotes: {
  //     method: 'POST',
  //     path: '/reprocessNotes',
  //     pathParams: z.object({
  //         integrationId: ObjectIdString
  //     }),
  //     body: z.object({
  //         query: AnyObject.optional(),
  //         limit: z.number().optional(),
  //         oldNlpDataVersionOnly: z.boolean().optional()
  //     }),
  //     responses: {
  //         200: z.unknown()
  //     }
  // },
  mapActivitiesForIntegration: {
    method: 'POST',
    path: '/mapActivitiesForIntegration',
    body: z.object({
      integrationId: ObjectIdString,
      activityType: ActivityType.default('Note'),
      limit: z.number().int().optional(),
      query: z.looseObject({}),
    }),
    responses: {
      200: z.unknown(),
    },
  },

  getCandidatesWithInboundNotes: {
    method: 'POST',
    path: '/getCandidatesWithInboundNotes/:integrationId',
    pathParams: z.object({
      integrationId: ObjectIdString,
    }),
    body: null,
    responses: {
      200: z.object({
        result: ObjectIdString.array(),
      }),
    },
  },
  ping,
} as const satisfies Record<string, AppRoute>;

export const oldAdminContract = c.router(oldAdminEndpoints, {
  pathPrefix: '/admin',
  baseHeaders: {
    Authorization: z.string().optional(),
  },
});

export const adminApiContract = c.router(
  {
    testEventHandler: {
      method: 'POST',
      path: '/testEventHandler',
      body: z.object({
        eventType: z.string(),
        subType: z.string(),
        params: z.looseObject({
          options: z.looseObject({}),
        }),
      }),
      responses: {
        200: z.object({
          result: z.unknown(),
        }),
      },
    },
    dataIqStartBatch: {
      method: 'POST',
      path: '/dataIq/startBatch',
      body: z.object({
        account: ObjectIdSchema,
        candidateIds: ObjectIdSchema.array().nullish(),
        sourceIds: z.string().array().nullish(),
        searchQuery: SearchEntityQuery.nullish(),
        checkForUpdateOptions: DataIqCheckForUpdateOptions,
      }),
      responses: {
        200: z.object({
          operation: OperationDetailDTO,
        }),
      },
    },
    dataIqReplayStepForMatchingOperations: {
      method: 'POST',
      path: '/dataIq/replayStepForMatchingOperations',
      body: z.object({
        query: z.record(z.string(), z.unknown()),
        stepName: OperationStepName,
        options: DataIqReplayStepOptions.optional(),
      }),
      responses: {
        200: z.unknown(),
      },
    },
    getBullhornMeta: {
      method: 'GET',
      path: '/bullhorn/:accountId/meta/:entity',
      pathParams: z.object({
        accountId: ObjectIdString,
        entity: z.string(),
      }),
      query: z.object({
        fields: z.string().array().optional(),
      }),
      responses: {
        200: z.object({
          fields: BullhornApiMeta.Field.array(),
        }),
      },
    },
    searchEntity: {
      method: 'GET',
      path: '/bullhorn/:accountId/search/:entity',
      pathParams: z.object({
        accountId: ObjectIdString,
        entity: z.string(),
      }),
      query: SearchEntityQuery,
      responses: {
        200: z.object({
          data: z.array(
            z
              .object({
                id: z.string().or(z.number()),
                name: z.string().optional(),
              })
              .passthrough()
          ),
          total: z.number(),
        }),
      },
    },
    referenceFieldOptions: {
      method: 'GET',
      path: '/dataIq/:accountId/eval/meta',
      pathParams: z.object({
        accountId: ObjectIdString,
      }),
      query: z.object({
        fieldName: z.string(),
      }),
      responses: {
        200: z.object({
          options: BullhornApiMeta.Options,
        }),
      },
    },
  },
  {
    pathPrefix: '/adminApi',
    baseHeaders: {
      Authorization: z.string().optional(),
    },
  }
);
